import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatPlate } from "@/lib/format";
import { scheduleUndoableDelete } from "@/lib/undoDelete";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, PencilSimple, Trash, Truck } from "@phosphor-icons/react";

export default function Trucks() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ no_polisi: "", transporter: "", supir: "", keterangan: "", aktif: true });

  const load = async () => {
    const { data } = await api.get("/trucks", { params: q ? { q } : {} });
    setItems(data);
  };
  useEffect(() => {
    load();
    // Bootstrap-once — `load` closes over `q` which is a controlled input
    // handled by explicit user submit, not by mount-time re-fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ no_polisi: "", transporter: "", supir: "", keterangan: "", aktif: true });
    setOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ no_polisi: t.no_polisi, transporter: t.transporter || "", supir: t.supir || "", keterangan: t.keterangan || "", aktif: t.aktif });
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/trucks/${editing.id}`, form);
        toast.success("Truk diperbarui");
      } else {
        await api.post("/trucks", form);
        toast.success("Truk ditambahkan");
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onDelete = (t) => {
    scheduleUndoableDelete({
      items, setItems, target: t,
      label: `Truk ${t.no_polisi}`,
      apiCall: () => api.delete(`/trucks/${t.id}`, { params: { hard: true } }),
    });
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl">
      <div className="flex flex-col md:flex-row md:items-end justify-between border-b border-border pb-6 mb-6 gap-4">
        <div>
          <div className="overline">Master Data</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2 flex items-center gap-3">
            <span className="w-11 h-11 rounded-full bg-orange-100 text-primary flex items-center justify-center shrink-0">
              <Truck size={22} weight="bold" />
            </span>
            Truk
          </h1>
        </div>
        <Button onClick={openNew} className="rounded-lg font-bold uppercase tracking-wider" data-testid="add-truck-button">
          <Plus size={16} className="mr-2" /> Tambah Truk
        </Button>
      </div>

      <div className="flex gap-2 mb-4">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Cari no polisi, transporter, supir..." className="rounded-lg h-11" data-testid="search-truck-input" />
        <Button onClick={load} className="rounded-lg">Cari</Button>
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-secondary/50 text-left">
            <tr>
              <th className="p-3 overline">No Polisi</th>
              <th className="p-3 overline">Transporter</th>
              <th className="p-3 overline">Supir</th>
              <th className="p-3 overline">Keterangan</th>
              <th className="p-3 overline">Status</th>
              <th className="p-3 overline">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {items.map((t) => (
              <tr key={t.id} className="border-b border-border hover:bg-secondary/50">
                <td className="p-3 font-mono font-bold">{t.no_polisi}</td>
                <td className="p-3">{t.transporter}</td>
                <td className="p-3">{t.supir}</td>
                <td className="p-3 text-muted-foreground">{t.keterangan}</td>
                <td className="p-3">
                  <span className={`status-pill ${t.aktif ? "status-termuat" : "status-belum"}`}>
                    {t.aktif ? "AKTIF" : "NONAKTIF"}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" className="rounded-lg" onClick={() => openEdit(t)}>
                      <PencilSimple size={14} />
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-lg text-destructive bg-destructive/5 border-destructive/40" onClick={() => onDelete(t)} data-testid={`delete-truck-${t.no_polisi}`} title="Hapus permanen">
                      <Trash size={14} weight="fill" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Belum ada data truk.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-lg max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Truk" : "Tambah Truk"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="truck-form">
            <div>
              <Label className="overline block mb-2">No Polisi *</Label>
              <Input value={form.no_polisi} onChange={(e) => setForm((f) => ({ ...f, no_polisi: formatPlate(e.target.value) }))} placeholder="BK1234AB (dash otomatis)" required className="rounded-lg h-11 font-mono font-bold" data-testid="input-truck-plate" />
            </div>
            <div>
              <Label className="overline block mb-2">Transporter</Label>
              <Input value={form.transporter} onChange={(e) => setForm((f) => ({ ...f, transporter: e.target.value }))} className="rounded-lg h-11" data-testid="input-truck-transporter" />
            </div>
            <div>
              <Label className="overline block mb-2">Nama Supir</Label>
              <Input value={form.supir} onChange={(e) => setForm((f) => ({ ...f, supir: e.target.value }))} className="rounded-lg h-11" data-testid="input-truck-supir" />
            </div>
            <div>
              <Label className="overline block mb-2">Keterangan</Label>
              <Textarea value={form.keterangan} onChange={(e) => setForm((f) => ({ ...f, keterangan: e.target.value }))} className="rounded-lg" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.aktif} onCheckedChange={(v) => setForm((f) => ({ ...f, aktif: v }))} />
              <Label>Aktif</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-lg" onClick={() => setOpen(false)}>Batal</Button>
              <Button type="submit" className="rounded-lg font-bold uppercase" data-testid="save-truck-button">Simpan</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
